import { useState } from 'react';
import { ApiFout, inloggen } from '../lib/api';
import Kader from '../components/Kader';

/**
 * Het inlogscherm van de beheerder.
 *
 * WAAROM DIT SCHERM BESTAAT. Hiervoor zat er een wachtwoordvenster van nginx
 * voor het beheer. Dat venster is op een iPhone niet door te komen: het
 * toetsenbord zet de eerste letter van de gebruikersnaam automatisch op een
 * hoofdletter, en basic auth is hoofdlettergevoelig. Op de Mac werkte het wel.
 * Een eigen scherm haalt die hele valkuil weg - en het e-mailadres gaat aan de
 * serverkant sowieso naar kleine letters.
 *
 * DAAROM STAAN HIER OOK DIE DRIE ATTRIBUTEN OP HET E-MAILVELD.
 * `autoCapitalize="none"`, `autoCorrect="off"` en `spellCheck={false}` zijn
 * geen opsmuk: zonder die drie doet de telefoon precies wat hierboven staat.
 *
 * EN DAAROM ZIT ER EEN OOGJE OP DE TOEGANGSCODE, sinds 18 september 2026. Op
 * 18 september kwam Giovanni er niet meer in en was niet te zien WAT hij nu
 * eigenlijk intypte - alleen bolletjes. Dezelfde klasse fout als het
 * nginx-venster hierboven: een telefoontoetsenbord dat iets anders doet dan je
 * denkt, en een veld dat je dat niet laat zien. Vijf missers en de rem zet je
 * een kwartier buiten, dus blind hertypen is hier duurder dan elders.
 */
export default function InlogPage({ opGelukt }: { opGelukt: () => void }) {
  const [email, setEmail] = useState('');
  const [geheim, setGeheim] = useState('');
  const [toonCode, setToonCode] = useState(false);
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);

  async function verzend(gebeurtenis: React.FormEvent): Promise<void> {
    gebeurtenis.preventDefault();
    if (bezig) return;
    setBezig(true);
    setFout(null);
    try {
      await inloggen(email, geheim);
      opGelukt();
    } catch (probleem) {
      setFout(probleem instanceof ApiFout ? probleem.message : 'Inloggen is niet gelukt.');
      // De code wissen en het adres laten staan. Bij een typefout hoef je niet
      // allebei opnieuw te typen, en op een telefoon scheelt dat echt.
      setGeheim('');
    } finally {
      setBezig(false);
    }
  }

  return (
    // DE UITLEG BOVEN EN HET CONTACT ONDER HET FORMULIER ZIJN VOOR GOOGLE, NIET
    // VOOR DE BEHEERDER. Op 29 oktober 2026 markeerde Safe Browsing heel
    // askgio.app als misleidend. Een publiek scherm dat alleen om een code
    // vraagt, zonder te zeggen wat het is of van wie, ziet er voor dat filter
    // uit als phishing. Dit is het enige scherm op `/`, dus hier moet het staan.
    // Geen link naar de privacyverklaring van de studiehulp: die gaat over een
    // andere dienst. De privacy van Tolk staat in de handleiding achter de "?".
    <Kader
      titel="Beheer"
      onderschrift="Ask Gio Tolk is een besloten tolkdienst van Ginovation in Venlo (KvK 12044783). Twee mensen voeren hier een gesprek, elk in hun eigen taal, en de app vertaalt elke beurt. Dit scherm is voor de beheerder; gasten krijgen een eigen gesprekslink."
      taalcode="nl"
    >
      {/* Een echt formulier, zodat "Enter" en de Ga-knop van de telefoon werken. */}
      <form onSubmit={(e) => void verzend(e)} className="space-y-4">
        <div>
          <label
            htmlFor="inlog-email"
            className="block text-[10px] uppercase tracking-widest text-slate-450 font-bold mb-1.5"
          >
            E-mailadres
          </label>
          <input
            id="inlog-email"
            type="email"
            inputMode="email"
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full p-2.5 bg-slate-50 border-2 border-slate-250 rounded-xl text-sm
                       text-slate-800 placeholder-slate-405 focus:outline-none focus:border-gio-blue
                       focus:bg-white transition-colors duration-155"
          />
        </div>

        <div>
          <label
            htmlFor="inlog-code"
            className="block text-[10px] uppercase tracking-widest text-slate-450 font-bold mb-1.5"
          >
            Toegangscode
          </label>
          {/* relative, want het oogje ligt OVER het veld. Het veld houdt rechts
              ruimte vrij (pr-11) zodat een lange code er niet onder verdwijnt. */}
          <div className="relative">
            <input
              id="inlog-code"
              type={toonCode ? 'text' : 'password'}
              autoComplete="current-password"
              // Dezelfde drie als bij het e-mailveld, en om dezelfde reden. Ze
              // doen op een verborgen veld niets, maar zodra het oogje openstaat
              // is het een gewoon tekstveld - en dan zet de telefoon er zonder
              // deze regels alsnog een hoofdletter voor.
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              value={geheim}
              onChange={(e) => setGeheim(e.target.value)}
              className="w-full p-2.5 pr-11 bg-slate-50 border-2 border-slate-250 rounded-xl text-sm
                         text-slate-800 focus:outline-none focus:border-gio-blue focus:bg-white
                         transition-colors duration-155"
            />
            {/*
              type="button", anders verstuurt hij het formulier: een knop in een
              form is standaard een verzendknop, en dan kost een druk op het
              oogje je een inlogpoging bij de rem.

              tabIndex={-1} houdt hem uit de tabvolgorde. Van code naar knop
              hoort rechtstreeks te gaan; wie geen muis gebruikt heeft aan het
              oogje niets.
            */}
            <button
              type="button"
              tabIndex={-1}
              onClick={() => setToonCode((aan) => !aan)}
              aria-label={toonCode ? 'Toegangscode verbergen' : 'Toegangscode tonen'}
              aria-pressed={toonCode}
              className="absolute right-1 top-1/2 -translate-y-1/2 p-2 rounded-lg
                         text-slate-450 hover:text-gio-blue transition-colors duration-155"
            >
              <Oog open={toonCode} />
            </button>
          </div>
          <p className="mt-1 text-[11px] text-slate-450 leading-relaxed">
            Tik op het oogje om te zien wat je intypt. Kijk wel even of niemand meekijkt.
          </p>
        </div>

        {fout !== null && (
          <p className="text-xs text-gio-danger bg-gio-danger/5 border-2 border-gio-danger/20 rounded-xl px-3 py-2">
            {fout}
          </p>
        )}

        <button
          type="submit"
          disabled={bezig || email === '' || geheim === ''}
          className="w-full py-3 rounded-xl bg-gio-orange hover:bg-gio-orange-deep text-white
                     font-bold text-sm transition-colors duration-155
                     disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {bezig ? 'Bezig…' : 'Inloggen'}
        </button>
      </form>

      <p className="mt-4 text-[11px] text-slate-450 leading-relaxed">
        Vragen? Mail naar{' '}
        <a href="mailto:team@askgio.nl" className="text-gio-blue underline">
          team@askgio.nl
        </a>
        . Hoe we met gesprekken omgaan, lees je in de handleiding onder het vraagteken.
      </p>
    </Kader>
  );
}

/**
 * Het oogje. Open als de code zichtbaar is, doorgestreept als hij verborgen is.
 *
 * ZELF GETEKEND EN GEEN ICONENPAKKET. Dit is het enige pictogram in de app dat
 * niet al een emoji of een tekst kan zijn; een bibliotheek erbij halen voor
 * twee paden is de ruil niet waard.
 *
 * `aria-hidden`: het oogje zegt niets extra's. Wat er staat te gebeuren, staat
 * in het `aria-label` van de knop eromheen, en dat wisselt mee.
 */
function Oog({ open }: { open: boolean }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
      <circle cx="12" cy="12" r="3" />
      {!open && <line x1="3" y1="21" x2="21" y2="3" />}
    </svg>
  );
}
